import React, { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { cropSquare } from "@/lib/image";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Images, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";

const IMG_EXTS = ["jpg", "jpeg", "png", "webp"];

const normalize = (s) =>
  (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

function guessPlayer(filename, players) {
  const base = normalize(filename.replace(/\.[^.]+$/, ""));
  const nums = base.match(/\d+/);
  if (nums) {
    const d = parseInt(nums[0], 10);
    const byDorsal = players.find((p) => p.dorsal === d);
    if (byDorsal) return byDorsal.id;
  }
  const byName = players.find((p) => {
    const first = normalize(p.name).split(/\s+/)[0];
    return first.length >= 3 && base.includes(first);
  });
  return byName ? byName.id : "";
}

export default function BulkPhotosDialog({ open, onOpenChange, players, onDone }) {
  const inputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState("");

  const sorted = [...players].sort((a, b) => (a.dorsal ?? 99) - (b.dorsal ?? 99));

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const valid = files.filter((f) =>
      IMG_EXTS.includes((f.name.split(".").pop() || "").toLowerCase()));
    if (valid.length < files.length) toast.warning("Se ignoraron archivos que no son JPG/PNG/WebP");
    setItems((prev) => [
      ...prev,
      ...valid.map((f) => ({
        file: f,
        url: URL.createObjectURL(f),
        playerId: guessPlayer(f.name, players),
      })),
    ]);
  };

  const close = (o) => {
    if (!o) {
      items.forEach((it) => URL.revokeObjectURL(it.url));
      setItems([]);
      setProgress("");
    }
    onOpenChange(o);
  };

  const uploadAll = async () => {
    const assigned = items.filter((it) => it.playerId);
    if (!assigned.length) {
      toast.error("Asigna al menos una foto a un jugador");
      return;
    }
    setUploading(true);
    let ok = 0;
    let fail = 0;
    for (let i = 0; i < assigned.length; i++) {
      const it = assigned[i];
      const player = players.find((p) => p.id === it.playerId);
      setProgress(`Subiendo ${i + 1} de ${assigned.length}: ${player?.name || ""}...`);
      try {
        const squared = await cropSquare(it.file);
        const fd = new FormData();
        fd.append("scope", "player_photo");
        fd.append("scope_id", it.playerId);
        fd.append("file", squared);
        const r = await api.post("/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
        await api.put(`/players/${it.playerId}`, {
          name: player.name,
          birth_year: player.birth_year ?? null,
          position: player.position || "",
          secondary_position: player.secondary_position || "",
          dorsal: player.dorsal ?? null,
          team_id: player.team_id,
          phone: player.phone || "",
          phone_mother: player.phone_mother || "",
          phone_father: player.phone_father || "",
          photo_id: r.data.id,
        });
        ok += 1;
      } catch (err) {
        fail += 1;
        toast.error(`${player?.name || it.file.name}: ${formatApiError(err)}`);
      }
    }
    setUploading(false);
    setProgress("");
    if (ok) toast.success(`${ok} foto${ok === 1 ? "" : "s"} asignada${ok === 1 ? "" : "s"}`);
    if (!fail) {
      close(false);
      onDone?.();
    } else {
      onDone?.();
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Images className="h-5 w-5 text-purple-600" /> Fotos masivas de jugadores
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Selecciona varias fotos a la vez. Si el nombre del archivo contiene el dorsal
            (ej. "7.jpg") o el nombre del jugador, se asigna automáticamente. Puedes corregir cada asignación.
          </DialogDescription>
        </DialogHeader>

        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
          onChange={onPick}
          className="hidden"
          data-testid="bulk-photos-input"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="border-purple-300 text-purple-700 hover:bg-purple-50 w-full border-dashed"
          data-testid="bulk-photos-pick"
        >
          <Upload className="h-4 w-4 mr-2" /> Elegir fotos ({items.length} seleccionadas)
        </Button>

        {items.length > 0 && (
          <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1" data-testid="bulk-photos-list">
            {items.map((it, idx) => (
              <div key={idx} className="flex items-center gap-3 border border-slate-200 rounded-md p-2">
                <img src={it.url} alt="" className="w-11 h-11 rounded-md object-cover shrink-0" />
                <div className="text-xs text-slate-600 truncate flex-1 min-w-0">{it.file.name}</div>
                <select
                  value={it.playerId}
                  onChange={(e) => setItems((prev) => prev.map((x, i) => (i === idx ? { ...x, playerId: e.target.value } : x)))}
                  disabled={uploading}
                  className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white max-w-[200px]"
                  data-testid={`bulk-photo-player-${idx}`}
                >
                  <option value="">Sin asignar</option>
                  {sorted.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.dorsal != null ? `${p.dorsal} · ` : ""}{p.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setItems((prev) => prev.filter((_, i) => i !== idx))}
                  disabled={uploading}
                  className="text-red-500 hover:text-red-700 p-1"
                  data-testid={`bulk-photo-remove-${idx}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {progress && <div className="text-xs font-semibold text-purple-700" data-testid="bulk-photos-progress">{progress}</div>}

        <DialogFooter className="flex flex-row justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => close(false)} disabled={uploading} data-testid="bulk-photos-cancel">
            Cancelar
          </Button>
          <Button
            onClick={uploadAll}
            disabled={uploading || !items.some((it) => it.playerId)}
            className="bg-purple-600 hover:bg-purple-700 text-white font-bold"
            data-testid="bulk-photos-upload"
          >
            {uploading ? "Subiendo..." : `Subir ${items.filter((it) => it.playerId).length} foto(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
